import { Link } from "@mui/material";
import { Wrapper } from "../../../../components/Wrapper/Wrapper";
import { useLocation, useNavigate } from "react-router-dom";
import { NavBar } from "../../../../components/NavBar/NavBar";
import Filters from "../../../../components/Filters/Filters";

import "./PartsConfiguration.css";
import { Expandable } from "../../../../components/Expandable/Expandable";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import PartConfigurationGrid from "./PartConfigurationGrid";
import { createPart, getParts } from "../../services/PartsConfigurationService";
import Item from "../../../../components/Lists/Item";
import Paginator from "../../../../components/Paginator/Paginator";
import { useSnackBar } from "../../../../hooks/useSnackBar";
import PartList from "./PartList";
import {
  PartConfigurationContext,
  PartConfigurationProvider,
} from "../../contexts/PartConfigurationContext";
import { DropdownsProvider } from "../../../../contexts/DropdownsContext";
import { PermissionsProvider } from "../../../../contexts/PermissionsContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useDispatch } from "react-redux";
import {
  newPart,
  setOrder,
  setSearch,
  updateFilters,
} from "../../../../redux/features/configuration/parts/partsConfigurationSlice";
import useDebounce from "../../../../hooks/useDebounce";
import {
  addTab,
  clearActiveTab,
} from "../../../../redux/features/TabsManager/tabSlice";
import { SortingButton } from "../../../../components/Sorting/SortingButton";
import { SortingProvider } from "../../../../contexts/SortingContext";
import { FiltersManager } from "../../../../components/FiltersManager/FiltersManager";

// const partTableHeaders = [
// 	{ label: "PN", property: "partNumber" },
// 	{ label: "PN Description", property: "description" },
// 	{ label: "Category", property: "categoryName" },
// 	{ label: "Subcategory", property: "subcategory" },
// 	{ label: "ATA", property: "ata", mapper: (item) => ((item.ATAChapter && item.ATASection) ? `${item.ATAChapter}-${item.ATASection}${item.ATAParagraph ? ("-" + item.ATAParagraph) : ""}` : '--') }
// 	// { label: "ATA Chapter", property: "chapter" },
// 	// { label: "ATA Section", property: "section" },
// 	// { label: "ATA Paragraph", property: "paragraph" }
// ]

// const mockdata = Array.from({ length: 3 }).map((_, i) => ({
// 	id: i,
// 	type: "Replaceable",
// 	part_number: "111-222-333",
// 	description: "Engine Assembly",
// 	subcategory: "ABCD",
// 	category_id: "Serialized",
// 	chapter: "12",
// 	section: "11",
// 	paragraph: "45",
// 	hazardous: i === 0 ? "Yes" : "No",
// 	ata: `12-11-45`
// }));

// const dataMapper = (list) => {
// 	return list.map(item => ({
// 		id: item.id,
// 		...partTableHeaders.reduce((obj, header) => ({
// 			...obj,
// 			[header.label]: header.mapper ? header.mapper(item) : (item[header.property] || '--')
// 		}), {})
// 	}))
// }

const dataMapper = (part) => ({
  PN: part.partNumber,
  "PN Description": part.description,
  Category: part.categoryName,
  "Sub Category": part.subCategoryName,
  ATA:
    part.ATAChapter && part.ATASection
      ? `${part.ATAChapter}-${part.ATASection}${
          part.ATAParagraph ? "-" + part.ATAParagraph : ""
        }`
      : "",
});

const mapper = {
  partNumber: "PN",
  description: "Description",
  categoryName: "Category",
  subCategoryName: "Sub Category",
  ata: "ATA",
};

// const ataFormatter = (part) => ((part.ATAChapter && part.ATASection) ? `${part.ATAChapter}-${part.ATASection}${part.ATAParagraph ? ("-" + part.ATAParagraph) : ""}` : '')
const ataFormatter = (part) =>
  part.ATAChapter
    ? `${part.ATAChapter}` +
      (part.ATASection
        ? `-${part.ATASection}` +
          (part.ATAParagraph ? `-${part.ATAParagraph}` : "")
        : "")
    : "";

const ataProperties = ["ATAChapter", "ATASection", "ATAParagraph"];
const ATAColumnNames = {
  ATAChapter: "chapter_number",
  ATASection: "section_number",
  ATAParagraph: "paragraph_number",
};

const FilterContainer = ({ filtersOpen, setFiltersOpen }) => {
  // const { onFiltersChange } = useContext(PartConfigurationContext);
  const dispatch = useDispatch();
  const onFiltersChange = (dropdownFilters, txtFilters) => {
    console.log(dropdownFilters, txtFilters);
    dispatch(
      updateFilters({
        filters: {
          dropdowns: dropdownFilters,
          txt: txtFilters,
        },
      })
    );
  };
  return (
    <Filters
      extOpen={filtersOpen}
      openChange={(value) => setFiltersOpen(value)}
      onApplyFilters={onFiltersChange}
      hideToggle
      dropdownIds={[1, 2, 3]}
    />
  );
};

const SearchBarContainer = ({ dataFromInventoryManagement }) => {
  const [inputValue, setInputValue] = useState("");
  const debouncedValue = useDebounce(inputValue, 250);
  const dispatch = useDispatch();

  const didMount = useRef(false);
  // const { onSearch } = useContext(PartConfigurationContext);

  const onSearch = (value) => {
    dispatch(setSearch(value));
  };

  const handleChange = (event) => {
    setInputValue(event.target.value);
  };

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true; // Evita la primera ejecución
      return;
    }
    onSearch(debouncedValue);
  }, [debouncedValue]);

  return <input placeholder="Search Part" onChange={handleChange} />;
};

const AddButtonContainer = ({ onSuccessCreation }) => {
  // const { addPart } = useContext(PartConfigurationContext);
  const { infoMsg, successMsg, warningMsg } = useSnackBar();
  const dispatch = useDispatch();
  const addPart = () => {
    infoMsg("New Part", "Creating Draft", 2000);
    dispatch(newPart(0)).then((response) => {
      onSuccessCreation();
    });
  };
  const { hasPermission } = usePermissions();
  return (
    hasPermission("create") && (
      <button className="filled iconTextButton" onClick={addPart}>
        <span>+</span> <span>New Part</span>
      </button>
    )
  );
};

export const PartsConfiguration = () => {
  const location = useLocation();
  const [partList, setPartList] = useState([]);
  const [partListBackup, setPartListBackup] = useState([]);
  const [editableList, setEditableList] = useState([]);
  const [currentlyEditing, setCurrentlyEditing] = useState(null);
  const [filters, setFilters] = useState(null);
  const listRef = useRef();
  const createRef = useRef();
  const navigate = useNavigate();
  const { dataFromInventoryManagement, dataFromManualSearch } = location.state || {};

  const { successMsg, warningMsg } = useSnackBar();
  function generateUUID() {
    return crypto.randomUUID();
  }

  useEffect(() => {
    if (dataFromManualSearch) {
      dispatch(setSearch(dataFromManualSearch));
      //delete location state
      location.state = {};
    } else {
      dispatch(setSearch(""));
    }
  }, [dataFromManualSearch]);
  
  useEffect(() => {
    dispatch(
      addTab({
        icon: "icon-warehouse",
        color: "#F16F6F",
        path: "/configuration/parts",
        title: "Parts",
        isPinned: false,
        componentKey: "PartsConfiguration",
        id: 4,
        isModule: false,
        date: generateUUID(),
      })
    );
  }, []);
  const generalSectionChange = (id, field, formValues) => {
    if (field)
      setEditableList((prev) => {
        const { name, value } = field;
        if (ataProperties.includes(name)) {
          let tempItem = { ...prev[id] };
          tempItem[name] = value[ATAColumnNames[name]];
          tempItem["ata"] = ataFormatter(tempItem);
          return { ...prev, [id]: tempItem };
        }
        if (name === "categoryId") {
          return {
            ...prev,
            [id]: { ...prev[id], ["categoryName"]: value.name },
          };
        }
        if (name === "subCategoryId") {
          return {
            ...prev,
            [id]: { ...prev[id], ["subCategoryName"]: value.name },
          };
        }
        return { ...prev, [id]: { ...prev[id], [name]: value } };
        // return prev;
      });
    setCurrentlyEditing({ id, ...formValues });
  };

  useEffect(() => {}, [currentlyEditing]);

  const savePart = useCallback(() => {
    createPart(currentlyEditing)
      .then((response) => {
        if (response.data.responseCode === 200) {
          successMsg("Part saved successfully");
        } else {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`
          );
        }
      })
      .catch((error) => warningMsg(`Something went wrong: ${error}`));
  }, [currentlyEditing]);

  const scrollTop = (e) => {
    // setTimeout(()=>{
    // 	// e.currentTarget.scrollIntoView(
    // 	// 	{ behavior: "smooth", block: "start"}
    // 	// );
    // }, 400)
  };

  const dispatch = useDispatch();
  const clearTabsAndNavigate = (path) => {
    dispatch(clearActiveTab());
    navigate(path);
  };
  const dataBreadCrumbs = [
    
    <Link
      key={2}
      underline="hover"
      sx={{ color: "#000000", cursor: "pointer", fontSize: "var(--p-size)" }}
      onClick={() => clearTabsAndNavigate("/secure/configuration")}
    >
      Configuration
    </Link>,
    <Link
      key={3}
      underline="none"
      sx={{ color: "#000000", cursor: "default", fontSize: "var(--p-size)" }}
    >
      Parts
    </Link>,
  ];

  const filtersChange = (dropdownFilters, txtFilters) => {
    setFilters({
      dropdowns: dropdownFilters,
      txt: txtFilters,
    });
  };

  const saveCallbacks = useMemo(
    () => ({
      general: savePart,
    }),
    [savePart]
  );

  const loadMoreParts = () => {};

  const [filtersOpen, setFiltersOpen] = useState(false);

  const openFirstRow = () => {
    let openEvent = new MouseEvent("click", {
      bubbles: false,
      cancelable: true,
      view: window,
    });
    listRef.current?.querySelector("summary")?.dispatchEvent(openEvent);
    listRef.current?.querySelector("summary")?.scrollIntoView();
  };

  const onOrderChange = (order) => {
    dispatch(setOrder(order));
  };

  return (
    <SortingProvider
      onOrderChange={onOrderChange}
      properties={{
        partNumber: "PN",
        description: "Description",
        categoryName: "Category",
        subCategoryName: "Sub Category",
        ATAChapterNumber: "ATA",
      }}
    >
      <PartConfigurationProvider>
        <PermissionsProvider resourceId={4}>
          <div className="parts-configuration-component" ref={listRef}>
            <div className="nav-header">
              <NavBar
                dataBreadCrumbs={"/configuration/parts"}
                currentPath={"/configuration/parts"}
                previousPath={"/onfiguration"}
                moduleId={1}
                goBackData={{
                  icon: "icon-configuration_module_icon",
                  color: "#7bb9db",
                  path: "/configuration",
                  title: "Configuration",
                  isPinned: false,
                  componentKey: "configuration",
                  id: -1,
                  isModule: true,
                  date: crypto.randomUUID(),
                }}
                searchInputProps={{
                  onTextChange: (e) => dispatch(setSearch(e)),
                }}
                isLocalSearch
                searchActions={
                  <>
                    <SortingButton />
                    <FiltersManager
                      onButtonClick={() => setFiltersOpen((prev) => !prev)}
                    />
                  </>
                }
                screenActions={
                  <AddButtonContainer onSuccessCreation={openFirstRow} />
                }
              />
            </div>
            <div className="parts-configuration-component-content">
              <div className="content-header">
                <FilterContainer
                  filtersOpen={filtersOpen}
                  setFiltersOpen={setFiltersOpen}
                />
                {/* <div className="parts-header">
                  <span className="left-container">
                    <i className="icon-warehouse"></i>
                    <b>All Parts</b>
                    <div className="search-bar-input">
                      <SearchBarContainer
                        dataFromInventoryManagement={dataFromInventoryManagement}
                      />
                      <i className="icon-search"></i>
                    </div>
                    <AddButtonContainer onSuccessCreation={openFirstRow} />
                    <button
                      onClick={() => setFiltersOpen((prev) => !prev)}
                      className="filter-toggle transparent"
                    >
                      <h2>Filters</h2>
                      <i className={`icon-arrow-down ${filtersOpen ? "open" : ""}`}></i>
                    </button>
                  </span>
                </div> */}
              </div>
              <DropdownsProvider
                noPreloadOpts={[
                  51, 52, 53, 50, 54, 56, 60, 67, 73, 4, 5, 6, 7, 8, 9, 10, 75,
                  77, 78, 80, 81, 86, 17, 19, 15, 74, 94, 95, 98, 114, 115,
                ]}
              >
                <PartList />
              </DropdownsProvider>
            </div>
            <div className="paginator-container"></div>
          </div>
        </PermissionsProvider>
      </PartConfigurationProvider>
    </SortingProvider>
  );
};
